import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Brain, Trash2, Info, AlertCircle, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { CortexEntry, CortexCategory } from './types'
import { getCortexEntries, deleteCortexEntry as apiDeleteCortexEntry } from './api'
import { useExpandable } from '../../shared/hooks/useExpandable'
import { useAsyncData } from '../../shared/hooks/useAsyncData'
import { BackButton, SectionHeading, AnimatedChevron } from '@tendbot/ui'
import EmptyState from './EmptyState'
import { defaultStagger, expandCollapse } from '@tendbot/ui'
import { relativeTime } from '@tendbot/ui'

const CATEGORY_META: Record<CortexCategory, { color: string }> = {
  preference:  { color: 'text-gold bg-gold/10 border-gold/20' },
  person:      { color: 'text-blue-400 bg-blue-400/10 border-blue-400/20' },
  decision:    { color: 'text-emerald bg-emerald/10 border-emerald/20' },
  thread:      { color: 'text-amber bg-amber/10 border-amber/20' },
  observation: { color: 'text-pearl bg-pearl/10 border-pearl/20' },
  journal:     { color: 'text-violet-400 bg-violet-400/10 border-violet-400/20' },
}

const CATEGORY_ORDER: CortexCategory[] = ['preference', 'person', 'decision', 'thread', 'observation', 'journal']

const BORDER_COLORS: Record<CortexCategory, string> = {
  preference:  'border-l-gold',
  person:      'border-l-blue-400',
  decision:    'border-l-emerald',
  thread:      'border-l-amber',
  observation: 'border-l-pearl',
  journal:     'border-l-violet-400',
}

interface CortexProps {
  onBack: () => void
}

export default function Cortex({ onBack }: CortexProps): React.JSX.Element {
  const { t } = useTranslation('cortex')
  const { t: tc } = useTranslation('common')
  const fetchEntries = useCallback(() => getCortexEntries(), [])
  const { data: fetchedData, loading, error, refetch } = useAsyncData(fetchEntries, [])
  const [entries, setEntries] = useState<CortexEntry[]>([])
  const [prevFetchedData, setPrevFetchedData] = useState<typeof fetchedData>(null)
  if (fetchedData && fetchedData !== prevFetchedData) {
    setPrevFetchedData(fetchedData)
    setEntries(fetchedData.entries)
  }
  const expandState = useExpandable()

  const handleDelete = async (entryId: string) => {
    const prev = entries
    setEntries(entries.filter(e => e.id !== entryId))
    try {
      await apiDeleteCortexEntry(entryId)
    } catch {
      setEntries(prev)
    }
  }

  const grouped = CATEGORY_ORDER
    .map(cat => ({ category: cat, entries: entries.filter(e => e.category === cat) }))
    .filter(g => g.entries.length > 0)

  const { container: containerVariants, item: itemVariants } = defaultStagger

  if (loading) {
    return (
      <div className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4">
        <div className="h-4 w-16 bg-charcoal/50 rounded animate-shimmer mb-4" />
        <div className="h-12 bg-charcoal/30 rounded-xl animate-shimmer mb-6" />
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 bg-charcoal/20 rounded-xl animate-shimmer mb-3" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4 space-y-6">
        <BackButton onClick={onBack} />
        <div className="glass-card rounded-xl p-6 text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-rose/10 flex items-center justify-center mx-auto">
            <AlertCircle size={24} className="text-rose" />
          </div>
          <p className="text-pearl/70 text-sm">{error}</p>
          <button
            onClick={refetch}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 text-gold text-sm font-medium hover:bg-gold/20 transition-colors"
          >
            <RefreshCw size={14} />
            {tc('actions.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full overflow-y-auto px-5 sm:px-8 lg:px-12 py-4"
    >
      <div className="max-w-2xl mx-auto">
      {/* Back button */}
      <BackButton onClick={onBack} />

      {/* Header */}
      <motion.div variants={itemVariants} className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
            <Brain size={20} className="text-gold" />
          </div>
          <div>
            <h2 className="font-display text-lg text-ivory font-semibold">{t('title')}</h2>
            <p className="text-pearl/60 text-xs">{t('factsLearned', { count: entries.length })}</p>
          </div>
        </div>
        <p className="text-pearl/50 text-sm leading-relaxed">
          {t('description')}
        </p>
      </motion.div>

      {/* Empty state */}
      {entries.length === 0 && (
        <motion.div variants={itemVariants} className="mb-6">
          <EmptyState
            icon={Brain}
            title={t('emptyTitle')}
            description={t('emptyDescription')}
          />
        </motion.div>
      )}

      {/* Category sections */}
      {grouped.map(group => {
        const meta = CATEGORY_META[group.category]
        return (
          <motion.div key={group.category} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <SectionHeading>{t(`categories.${group.category}`)}</SectionHeading>
            <div className="space-y-2">
              {group.entries.map(entry => {
                const isExpanded = expandState.isExpanded(entry.id)
                return (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    layout
                    className={`glass-card rounded-xl overflow-hidden border border-white/10 border-l-2 ${BORDER_COLORS[entry.category]}`}
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        {/* Content */}
                        <button
                          onClick={() => expandState.toggle(entry.id)}
                          className="flex-1 text-left min-w-0"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-semibold border ${meta.color}`}>
                              {t(`categorySingular.${entry.category}`)}
                            </span>
                            <span className="text-pearl/50 text-xs">
                              {relativeTime(entry.updatedAt)}
                            </span>
                          </div>
                          <p className="text-ivory text-sm leading-snug">{entry.summary}</p>
                        </button>

                        {/* Actions */}
                        <div className="flex items-center gap-2 shrink-0 mt-1">
                          <button onClick={() => expandState.toggle(entry.id)}>
                            <AnimatedChevron isOpen={isExpanded} className="text-pearl/55 hover:text-pearl/80 transition-colors" />
                          </button>
                          <button
                            onClick={() => handleDelete(entry.id)}
                            className="text-pearl/45 hover:text-rose transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Expanded content */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            {...expandCollapse}
                            className="overflow-hidden"
                          >
                            <div className="mt-3 pt-3 border-t border-white/5">
                              <p className="text-pearl/60 text-xs leading-relaxed">{entry.content}</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        )
      })}

      {/* Info footer */}
      <motion.div
        variants={itemVariants}
        className="glass-card rounded-xl p-4 border border-gold/10 mb-8"
      >
        <div className="flex items-start gap-3">
          <Info size={16} className="text-gold mt-0.5 shrink-0" />
          <div>
            <h4 className="text-ivory text-sm font-medium mb-1">{t('aboutTitle')}</h4>
            <p className="text-pearl/50 text-xs leading-relaxed">
              {t('aboutDescription')}
            </p>
          </div>
        </div>
      </motion.div>
      </div>
    </motion.div>
  )
}
